// ui/services/trueTodoAggregator.ts
// ✅ 覆蓋全文
//
// Goal
// - getTrueTodosOnce(): fetch + normalize + dedupe into { trueTodos, excluded }
// - MUST avoid "全量 query" on approval_requests / approvalFlows (Firestore rules 常會擋 -> 0)
//   => always query by module scope, matching each feature menu.
// - Backward compat: approval_requests vs approvalRequests collection name
//
// Notes
// - Dashboard UI (useTodosLiveMonitor) will FILTER OUT RAW_TODO items from KPI,
//   but we still fetch raw todos for debug visibility.
// - This file should never throw; errors are caught and returned as empty sets.

import { db } from "../../firebaseClient";
import {
  adaptRawTodoDocs,
  adaptApprovalRequestDocs,
  adaptApprovalFlowDocs,
  dedupeTrueTodos,
  type TrueTodoItem,
  type TrueTodoExcluded,
} from "./trueTodoAdapter";

const DEFAULT_LIMIT_PER_SOURCE = 200;

// compat fallback
const REQUEST_COLLECTIONS = ["approval_requests", "approvalRequests"];
const FLOW_COLLECTIONS = ["approvalFlows"];

// ⚠️ module-scoped list (aligns with todosSummary.classifyModule)
// Keep this list broad; unknown modules will be ignored by UI classifyModule anyway.
const DASH_MODULES: string[] = [
  // contracts
  "CONTRACT_REVIEW",
  "CONTRACT",
  // templates
  "TEMPLATE_STD",
  "TEMPLATE_CUSTOM",
  "TEMPLATES",
  // seals
  "SEAL_REQUEST",
  "SEAL",
  // official reply
  "OFFICIAL_REPLY",
  "GOV_CORRESPONDENCE",
  "GOV_CORRESP",
  // compliance intel
  "LAW_INFO",
  "INTEL",
  "COMPLIANCE_INTEL",
  // projects
  "GOV_PROJECT",
  "PROJECT",
  "PROJECT_CONTROL",
  // governance core docs
  "CORE_DOC",
  "GOVERNANCE_CORE_DOC",
  "GOVERNANCE_CORE",
  // cases
  "CASE",
  "CASES",
  "CASE_DISPUTE",
  "DISPUTE",
  "LITIGATION",
];

export type GetTrueTodosOnceOpts = {
  /** Backward compatible. If both exist, limitPerSource wins. */
  limit?: number;
  limitPerSource?: number;
};

function safeLimit(opts?: GetTrueTodosOnceOpts) {
  const n = Number(opts?.limitPerSource ?? opts?.limit ?? DEFAULT_LIMIT_PER_SOURCE);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : DEFAULT_LIMIT_PER_SOURCE;
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < (arr?.length || 0); i += size) out.push(arr.slice(i, i + size));
  return out;
}

function uniqById<T extends { id: string }>(arr: T[]): T[] {
  const map = new Map<string, T>();
  for (const x of arr || []) {
    const k = String(x?.id || "");
    if (!k) continue;
    if (!map.has(k)) map.set(k, x);
  }
  return Array.from(map.values());
}

async function fetchDocsSafe(collectionName: string, per: number): Promise<Array<{ id: string; data: any }>> {
  try {
    const snap = await db.collection(collectionName).orderBy("updatedAt", "desc").limit(per).get();
    return snap.docs.map((d: any) => ({ id: d.id, data: d.data() }));
  } catch {
    try {
      const snap = await db.collection(collectionName).limit(per).get();
      return snap.docs.map((d: any) => ({ id: d.id, data: d.data() }));
    } catch {
      return [];
    }
  }
}

async function fetchDocsByModules(
  collectionName: string,
  modules: string[],
  per: number
): Promise<Array<{ id: string; data: any }>> {
  const results: Array<{ id: string; data: any }> = [];
  const chunks = chunk(modules, 10);

  for (const group of chunks) {
    // 1) prefer where-in
    try {
      const snap = await db.collection(collectionName).where("module", "in", group).limit(per).get();
      results.push(...snap.docs.map((d: any) => ({ id: d.id, data: d.data() })));
      continue;
    } catch {
      // ignore and fallback below
    }

    // 2) fallback per module (more compatible with some rules/indexes)
    for (const m of group) {
      try {
        const snap = await db.collection(collectionName).where("module", "==", m).limit(per).get();
        results.push(...snap.docs.map((d: any) => ({ id: d.id, data: d.data() })));
      } catch {
        // ignore
      }
    }
  }

  return uniqById(results);
}

async function fetchFlowsByRequestIds(
  flowCollection: string,
  reqDocs: Array<{ id: string; data: any }>,
  per: number
): Promise<Array<{ id: string; data: any }>> {
  // If rules block flow queries, reading by docId often still works.
  // We derive candidate flow ids from request.module + request.id: `${module}_${targetId}`
  const out: Array<{ id: string; data: any }> = [];
  const seen = new Set<string>();
  const take = reqDocs.slice(0, Math.max(0, per)); // safety

  for (const r of take) {
    const rid = String(r?.id || "");
    if (!rid) continue;
    const raw = r?.data || {};
    const mod = String(raw?.module || "").trim().toUpperCase();

    const candidates = [
      String(raw?.flowId || ""),
      String(raw?.approvalFlowId || ""),
      mod ? `${mod}_${rid}` : "",
    ].filter(Boolean);

    for (const fid of candidates) {
      if (!fid) continue;
      if (seen.has(fid)) continue;
      seen.add(fid);
      try {
        const doc = await db.collection(flowCollection).doc(fid).get();
        if (doc?.exists) out.push({ id: doc.id, data: doc.data() });
      } catch {
        // ignore
      }
    }
  }

  return uniqById(out);
}

function buildRequestIndex(reqDocs: Array<{ id: string; data: any }>) {
  const idx: Record<string, any> = Object.create(null);
  for (const r of reqDocs || []) {
    const id = String(r.id || "");
    if (!id) continue;
    const raw = r.data || {};
    const mod = String(raw?.module || "").trim().toUpperCase();
    idx[id] = raw;
    if (mod) idx[`${mod}_${id}`] = raw;
  }
  return idx;
}

function buildFlowIndex(flowDocs: Array<{ id: string; data: any }>) {
  const idx: Record<string, any> = Object.create(null);
  for (const f of flowDocs || []) {
    const id = String(f.id || "");
    if (!id) continue;
    const raw = f.data || {};
    idx[id] = raw;

    const mod = String(raw?.module || "").trim().toUpperCase();
    const tid = raw?.targetId || raw?.requestId || raw?.approvalRequestId || null;

    if (tid) idx[String(tid)] = raw;
    if (mod && tid) idx[`${mod}_${String(tid)}`] = raw;

    // also index by "id looks like `${module}_${targetId}`"
    if (id.includes("_")) idx[id] = raw;
  }
  return idx;
}

// ---------------------------------------------------------------------------
// getTrueTodosOnce
// ---------------------------------------------------------------------------

export async function getTrueTodosOnce(
  opts: GetTrueTodosOnceOpts = {}
): Promise<{ trueTodos: TrueTodoItem[]; excluded: TrueTodoExcluded[] }> {
  const per = safeLimit(opts);
  const excluded: TrueTodoExcluded[] = [];
  const pushExcluded = (ex: TrueTodoExcluded) => excluded.push(ex);

  try {
    // --- RAW TODOS (todos)
    const todosDocs = await fetchDocsSafe("todos", per);

    // --- APPROVAL REQUESTS (module-scoped)
    const requestDocsMerged: Array<{ id: string; data: any }> = [];
    for (const col of REQUEST_COLLECTIONS) {
      // Always module-scoped first (to avoid rules returning 0)
      const scoped = await fetchDocsByModules(col, DASH_MODULES, per);
      requestDocsMerged.push(...scoped);
    }
    const requestDocs = uniqById(requestDocsMerged);

    // build request index first (so flows can be fetched by derived docId)
    const requestByKey = buildRequestIndex(requestDocs);

    // --- APPROVAL FLOWS
    let flowDocs: Array<{ id: string; data: any }> = [];

    // 1) Try module-scoped flow query (if flow docs have module field + rules allow)
    for (const col of FLOW_COLLECTIONS) {
      const scoped = await fetchDocsByModules(col, DASH_MODULES, per);
      flowDocs.push(...scoped);
    }
    flowDocs = uniqById(flowDocs);

    // 2) If flowDocs still empty (very common when flow docs don't have module field),
    //    fetch by derived docId from requests.
    if (!flowDocs.length) {
      for (const col of FLOW_COLLECTIONS) {
        const derived = await fetchFlowsByRequestIds(col, requestDocs, per);
        flowDocs.push(...derived);
      }
      flowDocs = uniqById(flowDocs);
    }

    const flowByKey = buildFlowIndex(flowDocs);

    // --- adapt
    const rawItems = adaptRawTodoDocs(todosDocs, pushExcluded);
    const reqItems = adaptApprovalRequestDocs(requestDocs, pushExcluded, flowByKey);
    const flowItems = adaptApprovalFlowDocs(flowDocs, pushExcluded, requestByKey);

    const trueTodos = dedupeTrueTodos([...rawItems, ...reqItems, ...flowItems]);

    return { trueTodos, excluded };
  } catch (err: any) {
    // never throw to UI
    excluded.push({
      source: "APPROVAL_FLOW",
      id: "AGGREGATOR_ERROR",
      reason: "UNKNOWN",
      raw: { message: err?.message || String(err) },
    });
    return { trueTodos: [], excluded };
  }
}

// ---------------------------------------------------------------------------
// subscribeTrueTodos (best-effort; dashboard recompute uses getTrueTodosOnce)
// ---------------------------------------------------------------------------

export function subscribeTrueTodos(
  onUpdate: (payload: { trueTodos: TrueTodoItem[]; excluded: TrueTodoExcluded[] }) => void,
  onError?: (err: any) => void,
  opts: { limitPerSource?: number } = {}
) {
  const perSource = Math.max(10, Math.min(opts.limitPerSource ?? DEFAULT_LIMIT_PER_SOURCE, 500));
  const excluded: TrueTodoExcluded[] = [];
  const pushExcluded = (ex: TrueTodoExcluded) => excluded.push(ex);

  let unsubscribers: Array<() => void> = [];
  let latestTodos: Array<{ id: string; data: any }> = [];
  let latestReq: Array<{ id: string; data: any }> = [];
  let latestFlow: Array<{ id: string; data: any }> = [];

  const recompute = () => {
    try {
      const requestByKey = buildRequestIndex(latestReq);
      const flowByKey = buildFlowIndex(latestFlow);

      const rawItems = adaptRawTodoDocs(latestTodos, pushExcluded);
      const reqItems = adaptApprovalRequestDocs(latestReq, pushExcluded, flowByKey);
      const flowItems = adaptApprovalFlowDocs(latestFlow, pushExcluded, requestByKey);
      const trueTodos = dedupeTrueTodos([...rawItems, ...reqItems, ...flowItems]);

      onUpdate({ trueTodos, excluded: [...excluded] });
    } catch (err) {
      onError?.(err);
    }
  };

  const watchCollectionByModules = (col: string, setter: (docs: Array<{ id: string; data: any }>) => void) => {
    const groups = chunk(DASH_MODULES, 10);

    for (const g of groups) {
      // prefer where-in
      try {
        const unsub = db
          .collection(col)
          .where("module", "in", g)
          .limit(perSource)
          .onSnapshot(
            (snap: any) => {
              const docs = snap.docs.map((d: any) => ({ id: d.id, data: d.data() }));
              // merge into current set (uniq by id)
              setter(uniqById([...(setter as any)._last || [], ...docs]));
              (setter as any)._last = (setter as any)._last || [];
              (setter as any)._last = uniqById([...(setter as any)._last, ...docs]);
              recompute();
            },
            (err: any) => onError?.(err)
          );
        unsubscribers.push(unsub);
        continue;
      } catch {
        // fallback per module
      }

      for (const m of g) {
        try {
          const unsub = db
            .collection(col)
            .where("module", "==", m)
            .limit(perSource)
            .onSnapshot(
              (snap: any) => {
                const docs = snap.docs.map((d: any) => ({ id: d.id, data: d.data() }));
                setter(uniqById([...(setter as any)._last || [], ...docs]));
                (setter as any)._last = (setter as any)._last || [];
                (setter as any)._last = uniqById([...(setter as any)._last, ...docs]);
                recompute();
              },
              (err: any) => onError?.(err)
            );
          unsubscribers.push(unsub);
        } catch {
          // ignore
        }
      }
    }
  };

  try {
    // raw todos
    try {
      const unsub = db.collection("todos").limit(perSource).onSnapshot(
        (snap: any) => {
          latestTodos = snap.docs.map((d: any) => ({ id: d.id, data: d.data() }));
          recompute();
        },
        (err: any) => onError?.(err)
      );
      unsubscribers.push(unsub);
    } catch {
      // ignore
    }

    // module-scoped requests
    for (const col of REQUEST_COLLECTIONS) {
      watchCollectionByModules(col, (docs) => (latestReq = docs));
    }

    // module-scoped flows (best effort)
    for (const col of FLOW_COLLECTIONS) {
      watchCollectionByModules(col, (docs) => (latestFlow = docs));
    }
  } catch (err) {
    onError?.(err);
  }

  return () => {
    for (const u of unsubscribers) {
      try {
        u();
      } catch {
        // ignore
      }
    }
    unsubscribers = [];
  };
}
 